/**
 * Bulk Actions Module
 *
 * Reusable bulk action handler for index pages with checkbox selection.
 * Auto-initializes on elements with data-controller="bulk-actions".
 *
 * Usage in Twig:
 * <div data-controller="bulk-actions"
 *      data-bulk-url="/products/bulk"
 *      data-resource-name="product">
 *     <input type="checkbox" data-bulk-select-all>
 *     <select data-bulk-action-select>
 *         <option value="">Bulk actions</option>
 *         <option value="delete">Delete</option>
 *     </select>
 *     <button data-bulk-apply>Apply</button>
 *     <input type="checkbox" data-bulk-checkbox value="123" data-name="Item Name">
 * </div>
 */

import { $, $$, on, ready, getData } from '../utils/dom.js';
import { post } from '../utils/api.js';
import { showNotification } from './notifications.js';

/**
 * Initialize bulk actions on a container
 * @param {Element} container - The container element
 */
function initBulkActions(container) {
    const config = getData(container);
    const bulkUrl = config.bulkUrl;
    const resourceName = config.resourceName || 'item';

    // Get elements
    const selectAll = $('[data-bulk-select-all]', container);
    const actionSelect = $('[data-bulk-action-select]', container);
    const applyBtn = $('[data-bulk-apply]', container);
    const checkboxes = () => $$('[data-bulk-checkbox]', container);

    if (!applyBtn) {
        console.warn('Bulk actions: Apply button not found');
        return;
    }

    /**
     * Update the select-all checkbox state
     */
    function updateSelectAllState() {
        if (!selectAll) return;

        const boxes = checkboxes();
        const checkedCount = boxes.filter(cb => cb.checked).length;

        selectAll.checked = boxes.length > 0 && checkedCount === boxes.length;
        selectAll.indeterminate = checkedCount > 0 && checkedCount < boxes.length;
    }

    /**
     * Update the apply button state
     */
    function updateApplyButton() {
        const checkedCount = checkboxes().filter(cb => cb.checked).length;
        applyBtn.disabled = checkedCount === 0;
        applyBtn.classList.toggle('opacity-50', checkedCount === 0);
        applyBtn.classList.toggle('cursor-not-allowed', checkedCount === 0);
    }

    /**
     * Get selected IDs and names
     */
    function getSelected() {
        const selected = checkboxes().filter(cb => cb.checked);
        return {
            ids: selected.map(cb => cb.value),
            names: selected.map(cb => cb.dataset.name || cb.value)
        };
    }

    /**
     * Execute the bulk action
     */
    async function executeBulkAction() {
        const action = actionSelect ? actionSelect.value : '';
        const { ids, names } = getSelected();

        if (!action) {
            showNotification('Please select an action', 'error');
            return;
        }

        if (ids.length === 0) {
            showNotification(`Please select at least one ${resourceName}`, 'error');
            return;
        }

        // Build confirmation message
        const actionLabels = {
            'delete': 'delete',
            'activate': 'activate',
            'deactivate': 'deactivate',
            'available': 'mark as available',
            'unavailable': 'mark as unavailable'
        };
        const actionText = actionLabels[action] || action;
        const plural = ids.length === 1 ? '' : 's';

        let confirmMessage = `Are you sure you want to ${actionText} ${ids.length} ${resourceName}${plural}?`;

        // Add extra warning for delete
        if (action === 'delete') {
            const nameList = names.slice(0, 5).join(', ');
            const more = names.length > 5 ? ` and ${names.length - 5} more` : '';
            confirmMessage += `\n\n${resourceName.charAt(0).toUpperCase() + resourceName.slice(1)}s: ${nameList}${more}\n\nThis action cannot be undone.`;
        }

        if (!confirm(confirmMessage)) {
            return;
        }

        // Show loading state
        const originalHtml = applyBtn.innerHTML;
        applyBtn.disabled = true;
        applyBtn.innerHTML = `
            <svg class="animate-spin h-4 w-4 mr-2 inline" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Processing...
        `;

        try {
            const response = await post(bulkUrl, { action, ids });

            if (response.success) {
                showNotification(response.message || 'Bulk action completed successfully', 'success');
                setTimeout(() => window.location.reload(), 1000);
            } else {
                throw new Error(response.message || 'Bulk action failed');
            }
        } catch (error) {
            console.error('Bulk action error:', error);
            showNotification(error.message || 'An error occurred while performing the bulk action', 'error');
            applyBtn.disabled = false;
            applyBtn.innerHTML = originalHtml;
        }
    }

    // Event listeners
    if (selectAll) {
        selectAll.addEventListener('change', function() {
            checkboxes().forEach(cb => cb.checked = this.checked);
            updateApplyButton();
        });
    }

    // Use event delegation for checkboxes (they may be dynamically added)
    on(container, 'change', '[data-bulk-checkbox]', () => {
        updateSelectAllState();
        updateApplyButton();
    });

    applyBtn.addEventListener('click', executeBulkAction);

    // Initial state
    updateApplyButton();
}

/**
 * Auto-initialize on page load
 */
ready(() => {
    $$('[data-controller="bulk-actions"]').forEach(initBulkActions);
});

/**
 * Manual initialization function
 * @param {Element|string} target - Element or selector
 */
export function init(target) {
    const element = typeof target === 'string' ? $(target) : target;
    if (element) {
        initBulkActions(element);
    }
}

export default {
    init
};
